import { defineMessages } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import type {
	FieldsEditorMessages,
	RequiredFieldMessages,
	EditIssueTypeMessages,
	NavigationMessages,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types.tsx';

export const fieldCategories = defineMessages({
	alwaysShowTitleRequestForm: {
		id: 'project-settings.product-features.service-desk.always-show-title-request-form',
		defaultMessage: 'Customer request form',
		description:
			'Title for the list of fields which are shown to customers when they make a request',
	},
	whatArePrimaryFieldsForInProductHelp: {
		id: 'project-settings.product-features.service-desk.what-are-primary-fields-for-in-product-help-with-forms-enabled-request-form',
		defaultMessage:
			'Fields and forms added to the request form are filled out by customers when they raise a request from the portal.{br}{br}{learnMoreLink}.',
		description: 'Explanation text inside tooltip - request form wording',
	},
	whatIsThis: {
		id: 'project-settings.product-features.service-desk.what-is-this',
		defaultMessage: 'What’s this?',
		description: 'Button that shows more details about field category',
	},
	linkToInProductHelpTextPortal: {
		id: 'project-settings.product-features.service-desk.link-to-in-product-help-text-portal',
		defaultMessage: 'Learn more about the portal',
		description:
			'Link text that leads to fields in product help documentation about the JSM portal',
	},
	primaryEmptyMessage: {
		id: 'project-settings-apps-issue-type-page-product-features.common.utils.service-desk-classic.primary-empty-message',
		defaultMessage: 'Drag and drop your fields here',
		description:
			'Instructions rendered under a placeholder image to encourage users to drag and drop different fields',
	},
});

export const fieldsEditorMessages: FieldsEditorMessages = defineMessages({
	sidebarHelperText: {
		id: 'jsd.project-settings-apps.issue-type-page.fields.fields-editor.helper-text',
		defaultMessage: 'Drag and drop these fields to the left area to customize this request type.',
		description: 'Explaining how to use drag and drop Field editor',
	},
	noActionFieldToolbarHeader: {
		id: 'jsd.project-settings-apps.issue-type-page.fields.droppable-list.field-toolbar-no-action.header',
		defaultMessage: "You can't remove this field",
		description: 'Displayed in the middle of dragging a field that cannot be removed',
	},
	noActionFieldToolbarDescription: {
		id: 'jsd.project-settings-apps.issue-type-page.fields.droppable-list.field-toolbar-no-action.message',
		defaultMessage:
			"{fieldCreator} created this field. You can't remove it from “{issueTypeName}” requests.",
		description: 'Displayed in the middle of dragging a field that cannot be removed',
	},
	noActionFieldToolbarDescriptionAppField: {
		id: 'jsd.project-settings-apps.issue-type-page.fields.droppable-list.field-toolbar-no-action-app-field.message',
		defaultMessage:
			"The {fieldCreator} app created this field. You can't remove it from “{issueTypeName}” requests.",
		description: 'Displayed in the middle of dragging a field that cannot be removed',
	},
	inactiveFieldsEmptyMessage: {
		id: 'jsd.project-settings-apps.issue-type-page.fields.droppable-list.inactive-field-list.empty-message',
		defaultMessage:
			'Drag unneeded fields here to remove them from your “{issueTypeName}” requests. Fields in this list are available to use later or on other request types.',
		description: 'Displayed when the "Choose existing field" list of fields is empty',
	},

	// error state; project-settings-apps/issue-type-page/view/view.js
	errorMessageTitle: {
		id: 'jsd.project-settings.project-settings-spa.issue-types-page.error-message-title',
		defaultMessage: "Something's gone wrong",
		description: 'Page failed to load, or some unknown error happened',
	},
	errorMessage: {
		id: 'jsd.project-settings.project-settings-spa.issue-types-page.error-message',
		defaultMessage: "We couldn't load this request type. Try refreshing the page.",
		description: 'Displayed to users when the page fails to load due to unexpected behaviour',
	},
	refreshButton: {
		id: 'jsd.project-settings.project-settings-spa.issue-types-page.refresh-button',
		defaultMessage: 'Try again',
		description: 'Wording inside the button to refresh the page and try again',
	},
	sharedFieldInfoMessage: {
		id: 'project-settings-apps.issue-type-page.field.info-used-in-other-request-type',
		defaultMessage: 'Other request types use this field.',
		description:
			'Info message shown to the user when they are trying to edit the name of a field that is also being used in another request type',
	},
	sharedFieldDialogMessage: {
		id: 'project-settings-apps.issue-type-page.field.dialog-used-in-other-request-type',
		defaultMessage: 'Some changes you make also affect these request types:',
		description:
			'Dialog message shown to the user when they are trying to edit the name of a field that is also being used in another request type',
	},
	sharedFieldLinkToDocumentationText: {
		id: 'project-settings-apps.issue-type-page.field.link-to-documentation-text-used-in-other-request-type',
		defaultMessage: 'Learn more',
		description: 'Link text that leads to fields documentation',
	},
	removeDropzoneHeader: {
		id: 'project-settings-apps-issue-type-page-product-features.common.utils.service-desk-classic.remove-dropzone-header',
		defaultMessage: 'Drop here to remove field from request form',
		description: 'Displayed in the middle of dragging a field from active fields to trash bin',
	},
	removeDropzoneDescription: {
		id: 'project-settings-apps-issue-type-page-product-features.common.utils.service-desk-classic.remove-dropzone-description',
		defaultMessage: 'Fields in this panel won’t appear in the portal for this request type.',
		description: 'Displayed in the middle of dragging a field from active fields to trash bin',
	},
});

export const requiredFieldMessages: RequiredFieldMessages = defineMessages({
	required: {
		id: 'jsd.project-settings-apps.issue-type-page.field.required',
		defaultMessage: 'Required',
		description: 'Label for checkbox marking a field as required; and lozenge in field card',
	},
	requiredDisabledAndCheckedTooltip: {
		id: 'jsd.project-settings-apps.issue-type-page.field.required-disabled-and-checked-tooltip',
		defaultMessage: '{fieldCreator} created and requires this field.',
		description:
			'Tooltip telling users that the field is marked as required and cannot be modified.',
	},
	requiredDisabledAndUncheckedTooltip: {
		id: 'jsd.project-settings-apps.issue-type-page.field.required-disabled-and-unchecked-tooltip',
		defaultMessage: '{fieldCreator} created this field. You can’t make it required.',
		description:
			'Tooltip telling users that the field is marked as not required and cannot be modified.',
	},
	requiredDisabledInAllCategoriesTooltip: {
		id: 'jsd.project-settings-apps.issue-type-page.field.required-disabled-in-all-categories-tooltip',
		defaultMessage: 'You can\'t make fields on "{issueType}" requests required.',
		description: 'Tooltip telling users that the field cannot be marked as required.',
	},
	requiredDisabledInCategoryTooltip: {
		id: 'jsd.project-settings-apps.issue-type-page.field.required-disabled-in-category-tooltip',
		defaultMessage:
			'Required fields can\'t be hidden. To make this field required, drag it into the "{goodList}" section above.',
		description:
			'Tooltip telling users that they must move a field to a different category before marking it as required.',
	},
	requiredTooltip: {
		id: 'project-settings-apps-issue-type-page-product-features.common.utils.service-desk-classic.required-tooltip',
		defaultMessage:
			'If checked, this field must be filled out when raising a request or creating an issue',
		description: 'Tooltip for checkbox marking a field as required',
	},
});

export const requiredFieldMessagesIssueTermRefresh: Pick<RequiredFieldMessages, 'requiredTooltip'> =
	defineMessages({
		requiredTooltip: {
			id: 'project-settings-apps-issue-type-page-product-features.common.utils.service-desk-classic.required-tooltip-issue-term-refresh',
			defaultMessage:
				'If checked, this field must be filled out when raising a request or creating a work item',
			description: 'Tooltip for checkbox marking a field as required',
		},
	});

export const editIssueTypeMessages: EditIssueTypeMessages & {
	backToRequestTypes: MessageDescriptor;
} = defineMessages({
	backToProject: {
		id: 'jsd.project-settings.project-settings-spa.flags.back-to-project-jsm',
		defaultMessage: 'Back to service project',
		description:
			'Text for a link that gets user back to the service project home; displayed in a small popup after save operation succeeded',
	},
	backToRequestTypes: {
		id: 'jsd.project-settings.project-settings-spa.flags.back-to-request-types',
		defaultMessage: 'Back to request types',
		description:
			'Text for a link that gets user back to the request types page; displayed in a small popup after save operation succeeded',
	},
	editIssueTypeFailureTitle: {
		id: 'jsd.project-settings.project-settings-spa.flags.edit-issue-type-failure-title-v2',
		defaultMessage: 'We couldn’t save your changes',
		description: 'UI flag title',
	},
	editIssueTypeFailureDescription: {
		id: 'jsd.project-settings.project-settings-spa.flags.edit-issue-type-failure-description-v2',
		defaultMessage: 'We couldn’t update the “{descriptionName}” issue type. Refresh and try again.',
		description: 'UI flag description',
	},

	descriptionLabel: {
		id: 'jsd.project-settings-apps.issue-type-page.main-header.description-inline-input.label',
		defaultMessage: 'Description',
		description: 'Inline input label',
	},
	editDescriptionButtonLabel: {
		id: 'jsd.project-settings-apps.issue-type-page.main-header.description-inline-input.edit-description-button-label',
		defaultMessage: 'Edit description',
		description: 'Label used by screen readers when focusing on the description input',
	},
	descriptionPlaceholder: {
		id: 'jsd.project-settings-apps.issue-type-page.main-header.description-inline-input.placeholder',
		defaultMessage: 'Let people know when to use this request type',
		description: 'Description inline input placeholder',
	},
	avatarAlt: {
		id: 'jsd.project-settings-apps.issue-type-page.main-header.avatar-icon.avatar-alt',
		defaultMessage: 'Request type icon',
		description: 'Icon input label',
	},
	editAvatar: {
		id: 'jsd.project-settings-apps.issue-type-page.main-header.avatar-icon.edit-avatar',
		defaultMessage: 'Edit request type icon',
		description:
			'Icon label. Shows on icon that opens dialog. Allows changing avatar of the currently visible Request type.',
	},
	editIssueTypeSuccessTitle: {
		id: 'jsd.project-settings.project-settings-spa.flags.edit-issue-type-success-title-new',
		defaultMessage: 'Request type updated',
		description: 'Title text for flag shown when the request type update completed successfully',
	},
	editIssueTypeSuccessDescription: {
		id: 'jsd.project-settings.project-settings-spa.flags.edit-issue-type-success-description-new',
		defaultMessage: '“{descriptionName}” has been successfully updated.',
		description:
			'Description text for flag shown when the request type update completed successfully. {descriptionName} is the name of the request type',
	},
	avatarAccessibleAlt: {
		id: 'project-settings-apps-issue-type-page-product-features.common.utils.service-desk-classic.avatar-accessible-alt',
		defaultMessage: 'Change request type icon',
		description: 'Icon input label',
	},
	avatarAriaLabel: {
		id: 'project-settings-apps-issue-type-page-product-features.common.utils.service-desk-classic.avatar-aria-label',
		defaultMessage: 'Change request type icon - {issueType}',
		description: 'Avatar button aria-label',
	},
	editNameButtonLabel: {
		id: 'project-settings-apps-issue-type-page-product-features.common.utils.service-desk-classic.edit-name-button-label',
		defaultMessage: 'Edit request type name',
		description: 'Label used by screen readers when focusing on the input',
	},
	editNameInputLabel: {
		id: 'project-settings-apps-issue-type-page-product-features.common.utils.service-desk-classic.edit-name-input-label',
		defaultMessage: 'Request type name',
		description: 'Label used by screen readers when input is focused',
	},
});

export const editIssueTypeMessagesIssueTermRefresh: Pick<
	EditIssueTypeMessages,
	'editIssueTypeFailureDescription'
> = defineMessages({
	editIssueTypeFailureDescription: {
		id: 'jsd.project-settings.project-settings-spa.flags.edit-issue-type-failure-description-v2-issue-term-refresh',
		defaultMessage: 'We couldn’t update the “{descriptionName}” work type. Refresh and try again.',
		description: 'UI flag description',
	},
});

type HiddenFieldMessages = {
	hidden: MessageDescriptor;
};

export const hiddenFieldMessages: HiddenFieldMessages = defineMessages({
	hidden: {
		id: 'project-settings-apps-issue-type-page-product-features.common.utils.service-desk-classic.hidden',
		defaultMessage: 'Hidden',
		description: 'Lozenge for showing fields that are hidden in the customer portal.',
	},
});

export const previewButton = defineMessages({
	// duplicated in `src/packages/project-settings/issue-type-page-product-features/src/common/utils/service-desk/messages.js`
	preview: {
		id: 'project-settings-apps.issue-type-page.sticky-bottom-controls.preview',
		defaultMessage: 'Preview',
		description: 'Button to open portal as a preview of request type configuration',
	},
});

export const viewButton = defineMessages({
	view: {
		id: 'project-settings-apps.issue-type-page.sticky-bottom-controls.view',
		defaultMessage: 'View',
		description: 'Button to open portal as a view of request type configuration',
	},
	viewInPortal: {
		id: 'project-settings-apps.issue-type-page.sticky-bottom-controls.viewInPortal',
		defaultMessage: 'View in Portal',
		description: 'Button to open portal as a view of request type configuration',
	},
	viewDisabledTooltip: {
		id: 'project-settings-apps.issue-type-page.sticky-bottom-controls.viewdiabled',
		defaultMessage: 'Request type can’t be viewed until changes are saved or discarded',
		description: 'Request type can’t be viewed in portal until changes are saved or discarded',
	},
});

export const linkedFieldsMessages = defineMessages({
	learnMore: {
		id: 'project-settings-apps.issue-type-page-product-features.common.utils.service-desk-classic.learnMore',
		defaultMessage: 'Learn more about linking form fields and Jira Fields.',
		description:
			'Link to an in-product help article describing how to configure fields in request types',
	},
	description: {
		id: 'project-settings-apps.issue-type-page-product-features.common.utils.service-desk-classic.message',
		defaultMessage:
			"This Jira field is linked to a form field below. It won't appear in some places in the portal.",
		description:
			"A message explaining that a field in a request type won't be displayed in the portal",
	},
});

export const navigationMessages: NavigationMessages = defineMessages({
	issueTypesLink: {
		id: 'jsd.project-settings.project-settings-spa.view-v2.nav-views.issue-types',
		defaultMessage: 'Request types',
		description:
			'Link text in navigation, and navigation section header, and a breadcrumb, and text of "Go back to Request types" button',
	},
	addIssueTypeDisabledTooltip: {
		id: 'jsd.project-settings.project-settings-spa.view-v2.nav-views.add-issue-type-disabled-tooltip',
		defaultMessage: "You can't add more than {limit} request types",
		description:
			'Tooltip displayed on the add request type button when the user has reached the maximum number of request types for a project',
	},
});
